import { useEffect, useState } from 'react';

import type { CookbookContext } from '@creditornot/cb-context';
import { useCookbookContext } from '@creditornot/cb-context';
import type { Direction, ThemeMode } from '@creditornot/cb-utils';
import { DATA_ATTR_THEME } from '@creditornot/cb-utils';

const isBrowser = typeof window !== 'undefined';

const themeObserverCallbacks: Array<(documentElement: HTMLElement) => void> = [];
let themeObserver: MutationObserver | undefined;

/* eslint-disable react-hooks/rules-of-hooks */
export const useThemeMode = (): ThemeMode => {
  const cookbookContext = useCookbookContext();

  if (cookbookContext) {
    return cookbookContext.themeMode;
  }

  if (!isBrowser) {
    return 'light';
  }

  const [value, setValue] = useState<ThemeMode>(
    (document.documentElement.getAttribute(DATA_ATTR_THEME) as ThemeMode | undefined) ?? 'light',
  );

  useEffect(() => {
    const updateAttribute = (element: HTMLElement) => {
      const attr = element.getAttribute(DATA_ATTR_THEME) as ThemeMode | undefined;

      if (attr) {
        setValue(attr);
      }
    };

    if (!themeObserver) {
      themeObserver = new MutationObserver((mutations) => {
        themeObserverCallbacks.forEach((callback) => callback(mutations[0].target as HTMLElement));
      });

      themeObserver.observe(document.documentElement, {
        attributeFilter: [DATA_ATTR_THEME],
      });
    }

    themeObserverCallbacks.push(updateAttribute);
    updateAttribute(document.documentElement); // Initial update

    return () => {
      const index = themeObserverCallbacks.indexOf(updateAttribute);
      if (index > -1) {
        themeObserverCallbacks.splice(index, 1);
      }
    };
  }, []);

  return value;
};

const directionObserverCallbacks: Array<(documentElement: HTMLElement) => void> = [];
let directionObserver: MutationObserver | undefined;

export const useDirection = (): Direction => {
  const cookbookContext = useCookbookContext();

  if (cookbookContext) {
    return cookbookContext.direction;
  }

  if (!isBrowser) {
    return 'ltr';
  }

  const [value, setValue] = useState<Direction>(
    (document.documentElement.getAttribute('dir') as Direction | undefined) ?? 'ltr',
  );

  useEffect(() => {
    const updateAttribute = (element: HTMLElement) => {
      const attr = element.getAttribute('dir') as Direction | undefined;

      if (attr) {
        setValue(attr);
      }
    };

    if (!themeObserver) {
      directionObserver = new MutationObserver((mutations) => {
        directionObserverCallbacks.forEach((callback) =>
          callback(mutations[0].target as HTMLElement),
        );
      });

      directionObserver.observe(document.documentElement, {
        attributeFilter: ['dir'],
      });
    }

    directionObserverCallbacks.push(updateAttribute);
    updateAttribute(document.documentElement); // Initial update

    return () => {
      const index = directionObserverCallbacks.indexOf(updateAttribute);
      if (index > -1) {
        directionObserverCallbacks.splice(index, 1);
      }
    };
  }, []);

  return value;
};

export const useAssertCookbookDomain = (domain: CookbookContext['domain']): void => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  const cookbookContext = useCookbookContext();

  if (process.env.NODE_ENV === 'test' && !cookbookContext) {
    return;
  }

  if (!cookbookContext) {
    throw new Error('useAssertCookbookDomain must be used inside CookbookProvider.');
  }

  const cookbookDomain = cookbookContext.domain;

  if (!cookbookDomain) {
    throw new Error(
      `Expected Cookbook domain: '${domain}'. Please configure the \`domain\` property for <CookbookProvider /> `,
    );
  }

  if (cookbookDomain !== domain) {
    throw new Error(
      `Invalid Cookbook domain: '${cookbookDomain}'. Your domain has been configured as '${domain}' for <CookbookProvider /> `,
    );
  }
};
